import { Alert, CopyButton, Progress, TextInput, Tooltip, useComputedColorScheme } from '@mantine/core';
import { IconCheck, IconCopy, IconInfoCircle } from '@tabler/icons-react';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { generateTimedOTP, type TimedOTP } from '~/logic/otp';

const BASE32_SECRET_LENGTH = 32;
const BASE32_REGEX = /^[A-Z2-7]+=*$/;

const normalizeSecret = (value: string): string => value.replaceAll(' ', '').toUpperCase().trim();

const formatSecret = (value: string): string =>
  value
    .match(/.{1,4}/g)
    ?.join(' ')
    .trim() ?? value;

const getValidationError = (rawValue: string): string | undefined => {
  const normalized = normalizeSecret(rawValue);
  if (!normalized) {
    return undefined;
  }
  if (normalized.length !== BASE32_SECRET_LENGTH) {
    return `Secret must be ${BASE32_SECRET_LENGTH} characters long`;
  }
  if (!BASE32_REGEX.test(normalized)) {
    return 'Secret must contain only base32 characters (A-Z and 2-7)';
  }
  return undefined;
};

export default function GoogleOtpSecretUtility() {
  const colorScheme = useComputedColorScheme();
  const isDark = colorScheme === 'dark';

  const [rawSecret, setRawSecret] = useState('');
  const [otp, setOtp] = useState<TimedOTP | undefined>(undefined);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);

  const normalizedSecret = useMemo(() => normalizeSecret(rawSecret), [rawSecret]);
  const validationError = useMemo(() => getValidationError(rawSecret), [rawSecret]);
  const isValidSecret = normalizedSecret.length === BASE32_SECRET_LENGTH && !validationError;

  useEffect(() => {
    if (!isValidSecret) {
      setOtp(undefined);
      setSecondsRemaining(0);
      return;
    }

    const interval = setInterval(() => {
      const nextOtp = generateTimedOTP(normalizedSecret);
      const nextSeconds = Math.max(0, Math.ceil((nextOtp.expiration.getTime() - Date.now()) / 1000));
      setOtp(nextOtp);
      setSecondsRemaining(nextSeconds);
    }, 100);

    return () => clearInterval(interval);
  }, [isValidSecret, normalizedSecret]);

  const progressPercent = (secondsRemaining / 30) * 100;

  return (
    <>
      <Head>
        <title>Google OTP Secret Helper | Stably</title>
        <meta
          name="description"
          content="Convert a Google Authenticator base32 secret into the current one-time verification code."
        />
      </Head>
      <main className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50 px-6 py-10 text-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100">
        <div className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white/95 p-7 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-400">
            Stably Utility
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Google OTP Secret Helper
          </h1>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
            Paste the 32-character Google authenticator secret and copy the current 6-digit verification code.
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Need setup steps?
            <Link
              href="https://docs.stably.ai/stably2/auth/auth-with-google#setting-up-a-google-test-account"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 font-medium text-cyan-700 underline decoration-cyan-300 underline-offset-2 hover:text-cyan-800 dark:text-cyan-300 dark:decoration-cyan-700 dark:hover:text-cyan-200"
            >
              Learn more in docs
            </Link>
            .
          </p>

          <div className="mt-8 space-y-4">
            <TextInput
              label="Google OTP secret"
              value={rawSecret}
              onChange={(event) => setRawSecret(event.currentTarget.value)}
              placeholder="ABCD EFGH IJKL MNOP QRST UVWX YZ23 4567"
              error={validationError}
              styles={{
                input: {
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  borderColor: isDark ? '#334155' : '#cbd5e1',
                  color: isDark ? '#f8fafc' : '#0f172a',
                },
                label: {
                  color: isDark ? '#f1f5f9' : '#0f172a',
                  fontWeight: 600,
                },
              }}
              rightSection={
                <Tooltip label="Use the plain-text secret shown after selecting “Can’t scan QR code” in Google">
                  <IconInfoCircle
                    size={16}
                    className="text-slate-400 dark:text-slate-300"
                  />
                </Tooltip>
              }
            />

            {normalizedSecret ?
              <p className="font-mono text-xs tracking-wide text-slate-500 dark:text-slate-300">
                Normalized: {formatSecret(normalizedSecret)}
              </p>
            : null}

            {!otp ?
              <Alert
                color="gray"
                variant="light"
                title="Enter a valid secret"
                className="dark:border dark:border-slate-700"
                icon={<IconInfoCircle size={16} />}
              >
                Once valid, this utility will generate the current one-time code and refresh every 30 seconds.
              </Alert>
            : <div className="rounded-xl border border-cyan-200 bg-cyan-50/90 p-5 dark:border-cyan-800 dark:bg-cyan-950/40">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">
                  Current verification code
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <p className="font-mono text-4xl font-semibold tracking-[0.24em] text-cyan-900 dark:text-cyan-100">
                    {otp.otp}
                  </p>
                  <CopyButton value={otp.otp}>
                    {({ copied, copy }) => (
                      <button
                        type="button"
                        onClick={copy}
                        className="inline-flex items-center gap-1 rounded-md border border-cyan-300 bg-white px-3 py-2 text-xs font-medium text-cyan-900 transition hover:bg-cyan-100 dark:border-cyan-700 dark:bg-slate-900 dark:text-cyan-100 dark:hover:bg-slate-800"
                      >
                        {copied ?
                          <IconCheck size={14} />
                        : <IconCopy size={14} />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    )}
                  </CopyButton>
                </div>

                <div className="mt-4">
                  <Progress
                    color="cyan"
                    value={progressPercent}
                  />
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">{secondsRemaining}s remaining</p>
                </div>
              </div>
            }
          </div>
        </div>
      </main>
    </>
  );
}
